import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { UserPlus, Radio, BarChart3, CheckCircle, ArrowLeft } from 'lucide-react';

const BRAND_PERKS = [
  'Bid on specific streamer audiences by CCV, genre, platform',
  'Source-level ad delivery — unblockable across all platforms',
  'Transparent CPM marketplace with real-time spend tracking',
  'Admin-reviewed quality control for brand safety',
];

export default function BrandSignup() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !password) { toast.error('Fill in all fields'); return; }
    if (password.length < 6) { toast.error('Password must be 6+ characters'); return; }
    setLoading(true);
    try {
      await register(name, email, password, ['brand']);
      toast.success('Welcome to AYO Source!');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Left: Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md animate-fade-in-up">
          <Link to="/landing" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-white transition-colors mb-8">
            <ArrowLeft className="w-4 h-4" /> Back to AYO Source
          </Link>

          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-[#7C3AED] flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-white">Brand Sign Up</h1>
              <p className="text-xs text-gray-500 font-mono uppercase tracking-widest">Advertiser Account</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label className="text-xs font-mono tracking-widest uppercase text-gray-500">Company / Brand Name</Label>
              <Input data-testid="brand-signup-name" type="text" value={name} onChange={e => setName(e.target.value)}
                placeholder="Acme Inc." className="bg-black/40 border-white/10 focus:border-[#7C3AED] focus:ring-1 focus:ring-[#7C3AED] text-white placeholder:text-gray-600 h-12" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-mono tracking-widest uppercase text-gray-500">Email</Label>
              <Input data-testid="brand-signup-email" type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="marketing@brand.com" className="bg-black/40 border-white/10 focus:border-[#7C3AED] focus:ring-1 focus:ring-[#7C3AED] text-white placeholder:text-gray-600 h-12" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-mono tracking-widest uppercase text-gray-500">Password</Label>
              <Input data-testid="brand-signup-password" type="password" value={password} onChange={e => setPassword(e.target.value)}
                placeholder="Min 6 characters" className="bg-black/40 border-white/10 focus:border-[#7C3AED] focus:ring-1 focus:ring-[#7C3AED] text-white placeholder:text-gray-600 h-12" />
            </div>
            <Button data-testid="brand-signup-submit" type="submit" disabled={loading}
              className="w-full h-12 bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-bold uppercase tracking-widest rounded-none transition-all duration-200">
              {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> :
                <span className="flex items-center gap-2"><UserPlus className="w-4 h-4" /> Create Brand Account</span>}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-500 text-sm">Already have an account? <Link to="/login" className="text-[#7C3AED] hover:underline font-medium">Sign In</Link></p>
            <p className="text-gray-600 text-xs mt-2">Want to monetize your stream? <Link to="/signup/creator" className="text-[#FF3B30] hover:underline">Creator Sign Up</Link></p>
          </div>
        </div>
      </div>

      {/* Right: Value Props */}
      <div className="hidden lg:flex flex-1 items-center justify-center bg-[#0a0a0a] border-l border-white/5 p-12">
        <div className="max-w-md">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#7C3AED]/30 rounded-full bg-[#7C3AED]/5 mb-8">
            <div className="w-2 h-2 bg-[#7C3AED] rounded-full" />
            <span className="text-xs font-mono text-[#7C3AED] uppercase tracking-widest">For Brands</span>
          </div>

          <h2 className="font-heading text-3xl font-black tracking-tighter text-white uppercase mb-6">
            Reach Audiences<br />At The Source
          </h2>

          <div className="space-y-4 mb-10">
            {BRAND_PERKS.map((perk, i) => (
              <div key={i} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#7C3AED] mt-0.5 flex-shrink-0" />
                <p className="text-sm text-gray-300 leading-relaxed">{perk}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 border border-white/10 rounded-lg bg-white/[0.02]">
              <p className="text-2xl font-black text-white font-mono">$3-25</p>
              <p className="text-xs text-gray-500 font-mono uppercase">CPM Range</p>
            </div>
            <div className="p-4 border border-white/10 rounded-lg bg-white/[0.02]">
              <p className="text-2xl font-black text-white font-mono">100%</p>
              <p className="text-xs text-gray-500 font-mono uppercase">View Rate</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
